import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { api } from "../api.ts";
import { date, dateTime } from "../format.ts";
import {
  Badge,
  Button,
  Card,
  Dialog,
  Empty,
  ErrorText,
  Field,
  Input,
  Loading,
  Notice,
  Page,
  Segmented,
  Select,
  Spinner,
  Stat,
  Table,
  Tabs,
  Td,
  Textarea,
} from "../ui.tsx";

/**
 * recoup's own team: which AI configuration does the work (open models too), how each scores
 * on the eval set, examples from real work, and what it all costs.
 */

type ProviderName =
  | "anthropic"
  | "openai"
  | "compatible"
  | "ollama"
  | "llamaextract"
  | "huggingface"
  | "mock";
type Models = { extract?: string; letter?: string; classify?: string; project?: string };
type Price = { input: number; output: number };

interface AiConfig {
  id: string;
  name: string;
  provider: ProviderName;
  baseUrl: string | null;
  models: Models;
  hasKey: boolean;
  prices: Record<string, Price>;
  notes: string;
  isDefault: boolean;
  createdBy: string;
  updatedAt: string;
  archivedAt: string | null;
}
interface ConfigList {
  configs: AiConfig[];
  environment: {
    name: string;
    provider: string;
    runtime: "local" | "e2b";
    /** Which providers have a key in Railway. */
    keys: Partial<Record<ProviderName, boolean>>;
  };
  orgs: { id: string; name: string; configId: string | null }[];
}

const PROVIDERS: Record<ProviderName, string> = {
  anthropic: "Anthropic (Claude)",
  llamaextract: "LlamaExtract (LlamaCloud): reads contracts, no other model needed",
  openai: "OpenAI",
  huggingface: "Hugging Face (open models)",
  compatible: "Open model (OpenAI-compatible API)",
  ollama: "Ollama (on your machine, development only)",
  mock: "Mock (no model, for testing)",
};
const TASK_MODELS: { key: keyof Models; label: string; hint: string }[] = [
  { key: "extract", label: "Reads contracts", hint: "The hardest job: use your strongest model." },
  { key: "letter", label: "Drafts claim letters and dispute replies", hint: "" },
  {
    key: "classify",
    label: "Reads vendor replies and sets up vendors",
    hint: "A small, fast model is enough.",
  },
];
const DEFAULT_MODELS: Partial<Record<ProviderName, Models>> = {
  anthropic: {
    extract: "claude-opus-5-5",
    letter: "claude-sonnet-5-5",
    classify: "claude-haiku-5-5",
  },
  openai: { extract: "gpt-6-astra", letter: "gpt-6.1-sol", classify: "gpt-6-luna" },
  ollama: { extract: "qwen3:8b", letter: "qwen3:8b", classify: "qwen3:8b" },
};
const HF_MODEL = "meta-llama/Llama-3.3-70B-Instruct";
const KIND: Record<string, string> = {
  contract_extraction: "Contract reads",
  claim_letter: "Claim letters",
  dispute_reply: "Dispute replies",
  reply_classification: "Vendor replies read",
  vendor_setup: "Vendor setups",
};
const TASK: Record<string, string> = {
  contracts: "Contracts",
  letters: "Letters",
  replies: "Replies",
  contract: "Contract",
  letter: "Claim letter",
  dispute: "Dispute reply",
  reply: "Vendor reply",
  setup: "Vendor setup",
};
const VERDICT: Record<string, { label: string; tone: "ok" | "warn" | "danger" | "neutral" }> = {
  good: { label: "Right", tone: "ok" },
  corrected: { label: "Needed fixing", tone: "warn" },
  failed: { label: "Wrong", tone: "danger" },
  thumbs_up: { label: "Rated right 👍", tone: "ok" },
  thumbs_down: { label: "Rated wrong 👎", tone: "danger" },
};

/** Millionths of a dollar, as dollars: cents when it's at least a dollar, else finer. */
export function usd(micros: string | number | null | undefined): string {
  if (micros === null || micros === undefined) return "—";
  const n = Number(micros) / 1e6;
  if (n === 0) return "$0";
  return n >= 1
    ? `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : `$${n.toFixed(4)}`;
}
const tokens = (n: string | number) => {
  const v = Number(n);
  return v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}k` : String(v);
};
const seconds = (ms: number | null | undefined) => (ms ? `${(ms / 1000).toFixed(1)}s` : "—");
const pct = (score: number | null | undefined) =>
  score === null || score === undefined ? "—" : `${Math.round(score * 100)}%`;

type Tab = "overview" | "accuracy" | "examples" | "cost" | "advanced";

export function AiAdmin() {
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as Tab) || "overview";
  const go = (t: Tab) => setParams(t === "overview" ? {} : { tab: t }, { replace: true });
  return (
    <Page
      title="AI"
      lead="Check that the AI reading contracts is on, accurate and affordable. Only recoup's own team sees this page."
    >
      <Tabs
        label="AI"
        tabs={[
          { id: "overview", label: "Overview" },
          { id: "accuracy", label: "Accuracy" },
          { id: "examples", label: "Real work" },
          { id: "cost", label: "Cost" },
          { id: "advanced", label: "Advanced" },
        ]}
        value={tab}
        onChange={go}
      >
        {tab === "overview" && <Overview go={go} />}
        {tab === "accuracy" && <Evals />}
        {tab === "examples" && <Examples />}
        {tab === "cost" && <Usage />}
        {tab === "advanced" && <Configs go={go} />}
      </Tabs>
    </Page>
  );
}

/** Which model does what, in a sentence. */
function describeSetup(provider: string, models: Models = {}): string {
  if (provider === "mock")
    return "No AI: recoup fills in demo answers instead of reading contracts.";
  if (provider === "llamaextract")
    return `LlamaExtract (${models.extract === "cost_effective" ? "cost-effective" : "agentic"}) reads contracts; claim letters come from recoup's checked template and vendor replies from its own rules, so no other model is needed.`;
  const m = { ...DEFAULT_MODELS[provider as ProviderName], ...models };
  const name = (id?: string) =>
    id?.startsWith("claude-")
      ? `Claude ${id.split("-")[1]![0]!.toUpperCase()}${id.split("-")[1]!.slice(1)}`
      : id;
  if (!m.extract) return "";
  return `${name(m.extract)} reads contracts, ${name(m.letter || m.extract)} drafts claim letters, ${name(m.classify || m.letter || m.extract)} reads vendor replies.`;
}

type EvalList = { runs: EvalRun[]; suite: Record<string, number> };

/** Run every case: what "Test accuracy" means. */
function useTestAccuracy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (configId: string | null) =>
      api.post<{ id: string }>("/operator/ai/evals", {
        configId,
        tasks: ["contracts", "letters", "replies"],
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["operator-ai-evals"] }),
  });
}
const useEvals = () =>
  useQuery({
    queryKey: ["operator-ai-evals"],
    queryFn: () => api.get<EvalList>("/operator/ai/evals"),
    // Keep up while a test is going.
    refetchInterval: (q) =>
      q.state.data?.runs.some((r) => r.status === "queued" || r.status === "running")
        ? 3000
        : false,
  });

const right = (r: EvalRun, task: string) => {
  const s = r.tasks.find((x) => x.task === task);
  return s ? `${s.passed} of ${s.cases} right` : "—";
};

/** The three questions, answered in a line each. */
function Overview({ go }: { go: (t: Tab) => void }) {
  const configs = useQuery({
    queryKey: ["operator-ai-configs"],
    queryFn: () => api.get<ConfigList>("/operator/ai/configs"),
  });
  const evals = useEvals();
  const usage = useQuery({
    queryKey: ["operator-ai-usage", "30"],
    queryFn: () => api.get<UsageData>("/operator/ai/usage?days=30"),
  });
  const examples = useQuery({
    queryKey: ["operator-ai-examples"],
    queryFn: () => api.get<ExampleList>("/operator/ai/examples"),
  });
  const test = useTestAccuracy();
  const error = configs.error ?? evals.error ?? usage.error ?? examples.error;
  if (error) return <ErrorText error={error} />;
  if (!configs.data || !evals.data || !usage.data || !examples.data) return <Loading />;

  const def = configs.data.configs.find((c) => c.isDefault && !c.archivedAt);
  const setup = def
    ? { id: def.id, name: def.name, provider: def.provider, models: def.models }
    : {
        id: null,
        name: configs.data.environment.name,
        provider: configs.data.environment.provider,
        models: {},
      };
  const on = setup.provider !== "mock";
  const latest = evals.data.runs.find((r) => r.config_id === setup.id);
  const testing = latest && (latest.status === "queued" || latest.status === "running");
  const count = (...v: string[]) =>
    examples.data.totals.filter((t) => v.includes(t.verdict)).reduce((a, t) => a + t.n, 0);
  const u = usage.data.totals;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Is the AI on?">
        <div className="flex flex-col gap-2">
          <div>
            <Badge tone={on ? "ok" : "danger"}>{on ? "On" : "Off"}</Badge>
          </div>
          <p className="text-lg font-semibold">{setup.name}</p>
          <p className="text-muted">{describeSetup(setup.provider, setup.models)}</p>
          {!on && (
            <Notice tone="danger">
              Set MODEL_PROVIDER to anthropic in Railway before a customer uploads a contract.
            </Notice>
          )}
        </div>
      </Card>

      <Card title="Is it accurate?">
        {testing ? (
          <p className="flex items-center gap-2">
            <Spinner /> Testing now: {latest.tasks.reduce((a, t) => a + t.cases, 0)} of{" "}
            {Object.values(evals.data.suite).reduce((a, n) => a + n, 0)} done. This takes a few
            minutes; the page updates itself.
          </p>
        ) : latest?.status === "done" ? (
          <div className="flex flex-col gap-2">
            <p className="font-display text-3xl font-bold">{pct(latest.score)}</p>
            <p className="text-muted">
              Contracts {right(latest, "contracts")} · Letters {right(latest, "letters")} · Replies{" "}
              {right(latest, "replies")}
            </p>
            <p className="text-sm text-muted">Tested {date(latest.created_at)}</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => go("accuracy")}>See mistakes</Button>
              <Button variant="ghost" busy={test.isPending} onClick={() => test.mutate(setup.id)}>
                Test again
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {latest?.status === "failed" ? (
              <Notice tone="danger">
                The last test didn't finish. {plainError(latest.error ?? "")}
              </Notice>
            ) : (
              <p className="text-muted">
                Not tested yet. recoup reads sample contracts where we already know the right
                answers and scores itself. It takes a few minutes and costs a few dollars.
              </p>
            )}
            <div>
              <Button variant="primary" busy={test.isPending} onClick={() => test.mutate(setup.id)}>
                Test accuracy
              </Button>
            </div>
          </div>
        )}
        <ErrorText error={test.error} />
      </Card>

      <Card title="How is it doing on real contracts?">
        {count("good", "corrected", "failed", "thumbs_up", "thumbs_down") === 0 ? (
          <p className="text-muted">
            Nothing yet. As customers approve contracts and rate what recoup read, you'll see here
            what it got right and what people had to fix.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <p>
              <span className="font-semibold">{count("good", "thumbs_up")}</span> right ·{" "}
              <span className="font-semibold">{count("corrected")}</span> needed fixing ·{" "}
              <span className="font-semibold">{count("failed", "thumbs_down")}</span> wrong
            </p>
            <div>
              <Button onClick={() => go("examples")}>See them</Button>
            </div>
          </div>
        )}
      </Card>

      <Card title="What has it cost?">
        <div className="flex flex-col gap-2">
          <p className="font-display text-3xl font-bold">{usd(u.cost_micros)}</p>
          <p className="text-muted">
            Last 30 days · {u.work} {u.work === 1 ? "piece" : "pieces"} of work
            {u.failed ? ` · ${u.failed} failed` : ""}
          </p>
          <div>
            <Button onClick={() => go("cost")}>See the breakdown</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// --------------------------------------------------------------------------------- usage

interface UsageRow {
  work: number;
  failed: number;
  calls: number;
  input_tokens: string;
  output_tokens: string;
  cost_micros: string;
  unpriced: number;
  avg_ms: number | null;
}
interface UsageData {
  days: number;
  totals: UsageRow;
  byDay: (UsageRow & { day: string })[];
  byKind: (UsageRow & { kind: string })[];
  byModel: (UsageRow & { provider: string; model: string; config: string })[];
  byOrg: (UsageRow & { id: string; name: string; plan: string })[];
  evals: { runs: number; cost_micros: string };
}

function Usage() {
  const [days, setDays] = useState<"7" | "30" | "90">("30");
  const q = useQuery({
    queryKey: ["operator-ai-usage", days],
    queryFn: () => api.get<UsageData>(`/operator/ai/usage?days=${days}`),
  });
  if (q.isError) return <ErrorText error={q.error} />;
  const d = q.data;
  return (
    <>
      <Segmented
        label="Period"
        options={[
          { id: "7", label: "7 days" },
          { id: "30", label: "30 days" },
          { id: "90", label: "90 days" },
        ]}
        value={days}
        onChange={setDays}
      />
      {!d ? (
        <Loading />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat
              label="Spent on models"
              value={usd(d.totals.cost_micros)}
              tone="accent"
              note={
                Number(d.evals.cost_micros)
                  ? `Plus ${usd(d.evals.cost_micros)} on ${d.evals.runs} eval runs`
                  : `Last ${d.days} days`
              }
            />
            <Stat
              label="Pieces of work"
              value={d.totals.work}
              note={d.totals.failed ? `${d.totals.failed} failed` : "None failed"}
            />
            <Stat
              label="Model calls"
              value={d.totals.calls}
              note={`${tokens(d.totals.input_tokens)} tokens in, ${tokens(d.totals.output_tokens)} out`}
            />
            <Stat label="Average time" value={seconds(d.totals.avg_ms)} note="Per piece of work" />
          </div>
          {d.totals.unpriced > 0 && (
            <Notice tone="warn">
              {d.totals.unpriced} pieces of work used a model recoup has no price for, so the spend
              above leaves them out. Add the model's prices to its configuration.
            </Notice>
          )}
          <Card
            title="Spend per day"
            description="What model work cost each day, across every company."
          >
            <CostChart days={d.byDay} />
          </Card>
          <Card title="By kind of work" flush>
            <UsageTable
              first="Work"
              rows={d.byKind.map((r) => ({ key: r.kind, label: KIND[r.kind] ?? r.kind, r }))}
            />
          </Card>
          <Card title="By model" description="Which configuration and model did the work." flush>
            <UsageTable
              first="Model"
              rows={d.byModel.map((r) => ({
                key: `${r.config}:${r.model}`,
                label: (
                  <>
                    <div className="font-medium">{r.model}</div>
                    <div className="text-muted">
                      {r.config} · {r.provider}
                    </div>
                  </>
                ),
                r,
              }))}
            />
          </Card>
          <Card title="By company" flush>
            <UsageTable
              first="Company"
              rows={d.byOrg.map((r) => ({
                key: r.id,
                label: (
                  <>
                    <div className="font-medium">{r.name}</div>
                    <div className="text-muted">{r.plan.replace(/_/g, " ")}</div>
                  </>
                ),
                r,
              }))}
            />
          </Card>
        </>
      )}
    </>
  );
}

function UsageTable({
  first,
  rows,
}: {
  first: string;
  rows: { key: string; label: ReactNode; r: UsageRow }[];
}) {
  return (
    <Table
      head={[first, "Done", "Failed", "Calls", "Tokens in / out", "Cost", "Per piece", "Avg time"]}
      empty={rows.length === 0 && <Empty title="No model work in this period." />}
    >
      {rows.map(({ key, label, r }) => (
        <tr key={key}>
          <Td>{label}</Td>
          <Td>{r.work}</Td>
          <Td className={r.failed ? "text-danger" : "text-muted"}>{r.failed}</Td>
          <Td>{r.calls}</Td>
          <Td className="whitespace-nowrap">
            {tokens(r.input_tokens)} / {tokens(r.output_tokens)}
          </Td>
          <Td className="whitespace-nowrap">
            {usd(r.cost_micros)}
            {r.unpriced > 0 && <div className="text-xs text-warn">{r.unpriced} unpriced</div>}
          </Td>
          <Td>{usd(Number(r.cost_micros) / Math.max(1, r.work + r.failed))}</Td>
          <Td>{seconds(r.avg_ms)}</Td>
        </tr>
      ))}
    </Table>
  );
}

/** One series of daily spend: bars on a zero baseline, a tooltip per day, no legend needed. */
function CostChart({ days }: { days: (UsageRow & { day: string })[] }) {
  const [hover, setHover] = useState<number | null>(null);
  // Drawn at its real width, so labels stay legible on a phone.
  const box = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(720);
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setW(Math.max(280, Math.round(e!.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (days.length === 0) return <p className="text-sm text-muted">No model work in this period.</p>;
  const values = days.map((d) => Number(d.cost_micros) / 1e6);
  const max = Math.max(...values, 0.0001);
  // Round the top of the axis up to a tidy figure.
  const step = 10 ** Math.floor(Math.log10(max));
  const top = Math.ceil(max / step) * step;
  const H = 200;
  const left = 56;
  const bottom = 24;
  const plotW = W - left - 8;
  const plotH = H - bottom - 8;
  const slot = plotW / days.length;
  const barW = Math.max(2, Math.min(28, slot - 2));
  const y = (v: number) => 8 + plotH - (v / top) * plotH;
  const ticks = [0, top / 2, top];
  const h = hover === null ? null : days[hover]!;
  return (
    <div ref={box} className="relative">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full"
        role="img"
        aria-label={`Spend per day, from ${days[0]!.day} to ${days.at(-1)!.day}, highest ${usd(max * 1e6)}.`}
        onMouseLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={left}
              x2={W - 8}
              y1={y(t)}
              y2={y(t)}
              className="stroke-line"
              strokeWidth={1}
            />
            <text x={left - 8} y={y(t) + 4} textAnchor="end" className="fill-muted text-xs">
              {usd(t * 1e6)}
            </text>
          </g>
        ))}
        {values.map((v, i) => {
          const x = left + i * slot + (slot - barW) / 2;
          const top = y(v);
          const height = 8 + plotH - top;
          const r = Math.min(4, barW / 2, height);
          return (
            // biome-ignore lint/a11y/noStaticElementInteractions: the table below carries the same figures
            <g key={days[i]!.day} onMouseEnter={() => setHover(i)}>
              {/* A hit area taller and wider than the bar. */}
              <rect x={left + i * slot} y={8} width={slot} height={plotH} fill="transparent" />
              {height > 0 && (
                <path
                  d={`M${x},${8 + plotH} V${top + r} Q${x},${top} ${x + r},${top} H${x + barW - r} Q${x + barW},${top} ${x + barW},${top + r} V${8 + plotH} Z`}
                  className={hover === i ? "fill-accent-strong" : "fill-accent"}
                />
              )}
            </g>
          );
        })}
        <text x={left} y={H - 6} className="fill-muted text-xs">
          {date(days[0]!.day)}
        </text>
        <text x={W - 8} y={H - 6} textAnchor="end" className="fill-muted text-xs">
          {date(days.at(-1)!.day)}
        </text>
      </svg>
      {h && (
        <div
          className="pointer-events-none absolute top-0 rounded-control border border-line bg-surface px-3 py-2 text-sm shadow-pop"
          style={{
            left: `min(calc(${((left + (hover! + 0.5) * slot) / W) * 100}% + 8px), calc(100% - 180px))`,
          }}
        >
          <div className="font-medium">{date(h.day)}</div>
          <div>{usd(h.cost_micros)}</div>
          <div className="text-muted">
            {h.work} done{h.failed ? `, ${h.failed} failed` : ""} · {h.calls} calls
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------- configurations

/** Ready-made setups: one click adds one and tests it. */
const CATALOG: {
  name: string;
  provider: ProviderName;
  models: Models;
  about: string;
  env: string;
}[] = [
  {
    name: "LlamaExtract (cost-effective)",
    provider: "llamaextract",
    models: { extract: "cost_effective" },
    about: "Reads contracts for the fewest credits.",
    env: "LLAMA_CLOUD_API_KEY",
  },
  {
    name: "LlamaExtract (agentic)",
    provider: "llamaextract",
    models: { extract: "agentic" },
    about: "Reads tables and layout best; about 3 times the credits.",
    env: "LLAMA_CLOUD_API_KEY",
  },
  {
    name: "Llama 3.3 70B (Hugging Face)",
    provider: "huggingface",
    models: { extract: "meta-llama/Llama-3.3-70B-Instruct" },
    about: "Meta's open model: a strong, low-cost all-rounder.",
    env: "HF_TOKEN",
  },
  {
    name: "Qwen 2.5 72B (Hugging Face)",
    provider: "huggingface",
    models: { extract: "Qwen/Qwen2.5-72B-Instruct" },
    about: "Alibaba's open model: good at tables and structured answers.",
    env: "HF_TOKEN",
  },
  {
    name: "DeepSeek V3 (Hugging Face)",
    provider: "huggingface",
    models: { extract: "deepseek-ai/DeepSeek-V3-0324" },
    about: "A large open model, close to the best closed ones.",
    env: "HF_TOKEN",
  },
  {
    name: "gpt-oss 120B (Hugging Face)",
    provider: "huggingface",
    models: { extract: "openai/gpt-oss-120b" },
    about: "OpenAI's open-weight model.",
    env: "HF_TOKEN",
  },
  {
    name: "Claude",
    provider: "anthropic",
    models: {},
    about: "Anthropic's models: Opus reads contracts, Sonnet and Haiku the rest.",
    env: "ANTHROPIC_API_KEY",
  },
  {
    name: "OpenAI",
    provider: "openai",
    models: {},
    about: "OpenAI's models, each sized to its task.",
    env: "OPENAI_API_KEY",
  },
];

function QuickAdd({ list, go }: { list: ConfigList; go: (t: Tab) => void }) {
  const qc = useQueryClient();
  const test = useTestAccuracy();
  const add = useMutation({
    mutationFn: async (p: (typeof CATALOG)[number]) => {
      const { id } = await api.post<{ id: string }>("/operator/ai/configs", {
        name: p.name,
        provider: p.provider,
        models: p.models,
        prices: {},
        notes: p.about,
      });
      await test.mutateAsync(id);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["operator-ai-configs"] });
      go("accuracy");
    },
  });
  const added = new Set(list.configs.filter((c) => !c.archivedAt).map((c) => c.name));
  return (
    <Card
      title="Add a model"
      description="One click adds it and tests its accuracy on recoup's examples. The results show under Accuracy in a few minutes; switch to it there if it does better."
    >
      {add.isError && <ErrorText error={add.error} />}
      <div className="grid gap-3 sm:grid-cols-2">
        {CATALOG.map((p) => {
          const ready = !!list.environment.keys?.[p.provider];
          const busy = add.isPending && add.variables?.name === p.name;
          return (
            <div
              key={p.name}
              className="flex flex-col gap-2 rounded-control border border-line p-3"
            >
              <div className="font-medium">{p.name}</div>
              <div className="flex-1 text-sm text-muted">{p.about}</div>
              {added.has(p.name) ? (
                <Badge>Added</Badge>
              ) : ready ? (
                <Button
                  variant="primary"
                  busy={busy}
                  disabled={add.isPending}
                  onClick={() => add.mutate(p)}
                >
                  Add and test
                </Button>
              ) : (
                <div className="text-sm text-muted">Add {p.env} in Railway first.</div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function Configs({ go }: { go: (t: Tab) => void }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["operator-ai-configs"],
    queryFn: () => api.get<ConfigList>("/operator/ai/configs"),
  });
  const [editing, setEditing] = useState<AiConfig | "new" | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["operator-ai-configs"] });
  const setDefault = useMutation({
    mutationFn: (configId: string | null) => api.put("/operator/ai/default", { configId }),
    onSuccess: refresh,
  });
  const setOrg = useMutation({
    mutationFn: (v: { orgId: string; configId: string | null }) =>
      api.put(`/operator/ai/orgs/${v.orgId}`, { configId: v.configId }),
    onSuccess: refresh,
  });
  const archive = useMutation({
    mutationFn: (id: string) => api.post(`/operator/ai/configs/${id}/archive`),
    onSuccess: refresh,
  });
  if (q.isError) return <ErrorText error={q.error} />;
  if (!q.data) return <Loading />;
  const { configs, environment, orgs } = q.data;
  const live = configs.filter((c) => !c.archivedAt);
  const def = live.find((c) => c.isDefault);
  return (
    <>
      {editing && (
        <ConfigDialog
          key={editing === "new" ? "new" : editing.id}
          initial={editing === "new" ? null : editing}
          sandboxed={environment.runtime === "e2b"}
          onClose={() => setEditing(null)}
        />
      )}
      <QuickAdd list={q.data} go={go} />
      <Card
        title="Your AI setups"
        description={`Everyone uses ${def?.name ?? environment.name}. To try another model, add it here, test its accuracy, then switch to it, for everyone or just your own test company.`}
        actions={<Button onClick={() => setEditing("new")}>Add another model…</Button>}
        flush
      >
        <Table
          head={["Name", "Provider", "Models", ""]}
          empty={
            configs.length === 0 && (
              <Empty title={`Only ${environment.name} so far.`}>
                Nothing to do here unless you want to compare another model.
              </Empty>
            )
          }
        >
          {configs.map((c) => (
            <tr key={c.id} className={c.archivedAt ? "opacity-60" : ""}>
              <Td>
                <div className="flex flex-wrap items-center gap-2 font-medium">
                  {c.name}
                  {c.isDefault && <Badge tone="accent">Everyone uses this</Badge>}
                  {c.archivedAt && <Badge>Archived</Badge>}
                </div>
                {c.notes && <div className="mt-0.5 max-w-xs text-muted">{c.notes}</div>}
              </Td>
              <Td>
                <div>{PROVIDERS[c.provider]}</div>
                {c.baseUrl && <div className="break-all text-muted">{c.baseUrl}</div>}
              </Td>
              <Td className="max-w-sm text-muted">{describeSetup(c.provider, c.models)}</Td>
              <Td>
                {!c.archivedAt && (
                  <div className="flex justify-end gap-2">
                    <Button onClick={() => setEditing(c)}>Edit</Button>
                    <Button
                      variant="ghost"
                      busy={archive.isPending && archive.variables === c.id}
                      onClick={() => archive.mutate(c.id)}
                    >
                      Archive
                    </Button>
                  </div>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
      {live.length > 0 && (
        <Card
          title="Who uses which"
          description="Every company uses the default unless you pick another setup for it."
        >
          <div className="flex flex-col gap-5">
            <Field label="Default for every company">
              {(id) => (
                <Select
                  id={id}
                  className="sm:w-96"
                  value={def?.id ?? ""}
                  disabled={setDefault.isPending}
                  onChange={(e) => setDefault.mutate(e.target.value || null)}
                >
                  <option value="">{environment.name}</option>
                  {live.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <ErrorText error={setDefault.error ?? setOrg.error ?? archive.error} />
            {orgs.length > 0 && (
              <div className="-mx-5 border-t border-line">
                <Table head={["Company", "AI setup"]}>
                  {orgs.map((o) => (
                    <tr key={o.id}>
                      <Td className="font-medium">{o.name}</Td>
                      <Td>
                        <Select
                          aria-label={`AI setup for ${o.name}`}
                          className="sm:w-80"
                          value={o.configId ?? ""}
                          disabled={setOrg.isPending}
                          onChange={(e) =>
                            setOrg.mutate({ orgId: o.id, configId: e.target.value || null })
                          }
                        >
                          <option value="">The default ({def?.name ?? environment.name})</option>
                          {live.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </Select>
                      </Td>
                    </tr>
                  ))}
                </Table>
              </div>
            )}
          </div>
        </Card>
      )}
    </>
  );
}

function ConfigDialog({
  initial,
  sandboxed,
  onClose,
}: {
  initial: AiConfig | null;
  sandboxed: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: initial?.name ?? "",
    provider: initial?.provider ?? ("compatible" as ProviderName),
    baseUrl: initial?.baseUrl ?? "",
    models: { ...initial?.models } as Models,
    apiKey: "",
    removeKey: false,
    prices: { ...initial?.prices } as Record<string, Price>,
    notes: initial?.notes ?? "",
  });
  const set = (p: Partial<typeof form>) => setForm((f) => ({ ...f, ...p }));
  const models = [...new Set(Object.values(form.models).filter((m): m is string => !!m?.trim()))];
  const priced = ["compatible", "openai", "huggingface"].includes(form.provider);
  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name,
        provider: form.provider,
        baseUrl:
          form.provider === "compatible" || form.provider === "ollama"
            ? form.baseUrl || null
            : null,
        models: Object.fromEntries(Object.entries(form.models).filter(([, v]) => v?.trim())),
        apiKey: form.removeKey ? null : form.apiKey || undefined,
        prices: Object.fromEntries(
          Object.entries(form.prices).filter(
            ([m, p]) => models.includes(m) && (p.input || p.output),
          ),
        ),
        notes: form.notes,
      };
      return initial
        ? api.put(`/operator/ai/configs/${initial.id}`, body)
        : api.post("/operator/ai/configs", body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["operator-ai-configs"] });
      onClose();
    },
  });
  return (
    <Dialog
      open
      title={initial ? `Edit ${initial.name}` : "Add an AI setup"}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            busy={save.isPending}
            disabled={!form.name}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </>
      }
    >
      <Field
        label="Name"
        hint="What you'll see in usage and evals, e.g. Llama 3.3 70B on Together."
      >
        {(id) => (
          <Input id={id} value={form.name} onChange={(e) => set({ name: e.target.value })} />
        )}
      </Field>
      <Field label="Provider">
        {(id) => (
          <Select
            id={id}
            value={form.provider}
            onChange={(e) => {
              const provider = e.target.value as ProviderName;
              // Hugging Face starts on a model that reads contracts well; change it freely.
              const models =
                provider === "huggingface" && !form.models.extract
                  ? { ...form.models, extract: HF_MODEL }
                  : form.models;
              set({ provider, models });
            }}
          >
            {Object.entries(PROVIDERS)
              .filter(([p]) => !(sandboxed && p === "ollama"))
              .map(([p, label]) => (
                <option key={p} value={p}>
                  {label}
                </option>
              ))}
          </Select>
        )}
      </Field>
      {(form.provider === "compatible" || form.provider === "ollama") && (
        <Field
          label="API address"
          hint={
            form.provider === "ollama"
              ? "Where Ollama listens; empty for http://127.0.0.1:11434."
              : "The address that serves /chat/completions: https://api.together.xyz/v1, https://api.fireworks.ai/inference/v1, https://api.groq.com/openai/v1, https://openrouter.ai/api/v1, or your own vLLM."
          }
        >
          {(id) => (
            <Input
              id={id}
              value={form.baseUrl}
              placeholder="https://"
              onChange={(e) => set({ baseUrl: e.target.value })}
            />
          )}
        </Field>
      )}
      {form.provider === "llamaextract" && (
        <>
          <Field
            label="Reading quality"
            hint="Agentic reads tables and layout best; cost-effective uses fewer credits."
          >
            {(id) => (
              <Select
                id={id}
                value={form.models.extract || "agentic"}
                onChange={(e) => set({ models: { ...form.models, extract: e.target.value } })}
              >
                <option value="agentic">Agentic</option>
                <option value="cost_effective">Cost-effective</option>
              </Select>
            )}
          </Field>
          <Field
            label="LlamaCloud project ID"
            hint="Optional: in the console's address after /project/. Empty uses your default project, or LLAMA_CLOUD_PROJECT_ID."
          >
            {(id) => (
              <Input
                id={id}
                value={form.models.project ?? ""}
                onChange={(e) => set({ models: { ...form.models, project: e.target.value } })}
              />
            )}
          </Field>
        </>
      )}
      {form.provider !== "mock" &&
        form.provider !== "llamaextract" &&
        TASK_MODELS.map((m) => (
          <Field
            key={m.key}
            label={m.label}
            hint={
              m.hint ||
              (m.key !== "extract" && ["compatible", "huggingface"].includes(form.provider)
                ? "Empty uses the contracts model."
                : undefined)
            }
          >
            {(id) => (
              <Input
                id={id}
                value={form.models[m.key] ?? ""}
                placeholder={
                  DEFAULT_MODELS[form.provider]?.[m.key] ??
                  (m.key !== "extract"
                    ? ""
                    : form.provider === "huggingface"
                      ? HF_MODEL
                      : "meta-llama/Llama-3.3-70B-Instruct-Turbo")
                }
                onChange={(e) => set({ models: { ...form.models, [m.key]: e.target.value } })}
              />
            )}
          </Field>
        ))}
      {form.provider !== "mock" && form.provider !== "ollama" && (
        <Field
          label="API key"
          hint={
            initial?.hasKey
              ? "Saved and sealed. Leave empty to keep it."
              : form.provider === "compatible"
                ? "The model host's key. It's sealed and never shown again."
                : form.provider === "llamaextract"
                  ? "Your LlamaCloud key (llx-…). Empty uses LLAMA_CLOUD_API_KEY from Railway."
                  : form.provider === "huggingface"
                    ? "A Hugging Face access token (hf_…). Empty uses HF_TOKEN from Railway."
                    : "Empty uses the environment's key for this provider."
          }
        >
          {(id) => (
            <Input
              id={id}
              type="password"
              autoComplete="off"
              value={form.apiKey}
              disabled={form.removeKey}
              onChange={(e) => set({ apiKey: e.target.value })}
            />
          )}
        </Field>
      )}
      {initial?.hasKey && (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.removeKey}
            onChange={(e) => set({ removeKey: e.target.checked, apiKey: "" })}
          />
          Remove the saved key
        </label>
      )}
      {priced && models.length > 0 && (
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">Prices, US dollars per million tokens</legend>
          <p className="text-xs text-muted">
            recoup prices Claude itself. For other models, enter the host's prices, or their cost
            shows as unknown.
          </p>
          {models.map((m) => (
            <div key={m} className="grid grid-cols-[1fr_6rem_6rem] items-center gap-2 text-sm">
              <span className="truncate" title={m}>
                {m}
              </span>
              {(["input", "output"] as const).map((k) => (
                <Input
                  key={k}
                  aria-label={`${m} ${k} price`}
                  inputMode="decimal"
                  placeholder={k === "input" ? "In" : "Out"}
                  value={form.prices[m]?.[k] ?? ""}
                  onChange={(e) =>
                    set({
                      prices: {
                        ...form.prices,
                        [m]: {
                          input: 0,
                          output: 0,
                          ...form.prices[m],
                          [k]: Number(e.target.value) || 0,
                        },
                      },
                    })
                  }
                />
              ))}
            </div>
          ))}
        </fieldset>
      )}
      <Field label="Notes">
        {(id) => (
          <Textarea
            id={id}
            rows={2}
            value={form.notes}
            onChange={(e) => set({ notes: e.target.value })}
          />
        )}
      </Field>
      <ErrorText error={save.error} />
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------- evals

interface EvalRun {
  id: string;
  config_id: string | null;
  config_name: string;
  provider: string;
  models: Models;
  status: "queued" | "running" | "done" | "failed";
  started_by: string;
  cases: number;
  passed: number;
  score: number | null;
  cost_micros: string | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
  tasks: { task: string; score: number; passed: number; cases: number }[];
}
interface EvalResult {
  task: string;
  case_id: string;
  title: string;
  ok: boolean;
  score: number;
  checks: { field: string; expected: unknown; got: unknown; pass: boolean }[];
  error: string | null;
  models: string | null;
  input_tokens: number;
  output_tokens: number;
  cost_micros: string | null;
  duration_ms: number;
}

const show = (v: unknown) =>
  v === null || v === undefined ? "none" : typeof v === "string" ? v : JSON.stringify(v);

function Evals() {
  const configs = useQuery({
    queryKey: ["operator-ai-configs"],
    queryFn: () => api.get<ConfigList>("/operator/ai/configs"),
  });
  const runs = useEvals();
  const test = useTestAccuracy();
  const [configId, setConfigId] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  if (runs.isError) return <ErrorText error={runs.error} />;
  if (!runs.data || !configs.data) return <Loading />;
  const { suite } = runs.data;
  const live = configs.data.configs.filter((c) => !c.archivedAt);
  const chosen = configId ?? live.find((c) => c.isDefault)?.id ?? "";
  return (
    <>
      {open && <EvalDetail runId={open} onClose={() => setOpen(null)} />}
      <Card
        title="Test accuracy"
        description={`recoup reads ${suite.contracts} sample contracts, drafts ${suite.letters} claim letters and reads ${suite.replies} vendor replies where we already know the right answers, then scores itself. It takes a few minutes.`}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
          {live.length > 0 && (
            <Field label="AI setup">
              {(id) => (
                <Select
                  id={id}
                  className="sm:w-80"
                  value={chosen}
                  onChange={(e) => setConfigId(e.target.value)}
                >
                  <option value="">{configs.data.environment.name}</option>
                  {live.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          )}
          <div>
            <Button
              variant="primary"
              busy={test.isPending}
              onClick={() => test.mutate(chosen || null)}
            >
              Test accuracy
            </Button>
          </div>
        </div>
        <ErrorText error={test.error} />
      </Card>
      <Card title="Past tests" flush>
        <Table
          head={["Date", "AI setup", "Score", "Contracts", "Letters", "Replies", ""]}
          empty={
            runs.data.runs.length === 0 && (
              <Empty title="No tests yet.">Click Test accuracy to run the first one.</Empty>
            )
          }
        >
          {runs.data.runs.map((r) => (
            <tr key={r.id}>
              <Td className="whitespace-nowrap">
                <span title={dateTime(r.created_at)}>{date(r.created_at)}</span>
              </Td>
              <Td className="font-medium">{r.config_name}</Td>
              <Td className="whitespace-nowrap">
                {r.status === "done" ? (
                  <>
                    <span className="font-semibold">{pct(r.score)}</span>
                    {r.cost_micros && Number(r.cost_micros) > 0 && (
                      <div className="text-xs text-muted">cost {usd(r.cost_micros)}</div>
                    )}
                  </>
                ) : r.status === "failed" ? (
                  <>
                    <Badge tone="danger">Didn't finish</Badge>
                    {r.error && (
                      <div className="max-w-xs text-xs text-danger">{plainError(r.error)}</div>
                    )}
                  </>
                ) : (
                  <>
                    <Badge tone="warn">Testing…</Badge>
                    <div className="mt-1 text-xs text-muted">
                      {r.tasks.reduce((a, t) => a + t.cases, 0)} of{" "}
                      {Object.values(suite).reduce((a, n) => a + n, 0)} done
                    </div>
                  </>
                )}
              </Td>
              {["contracts", "letters", "replies"].map((t) => (
                <Td key={t} className="whitespace-nowrap">
                  {right(r, t)}
                </Td>
              ))}
              <Td>
                {r.status !== "queued" && r.status !== "running" && r.cases > 0 && (
                  <Button onClick={() => setOpen(r.id)}>See details</Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Card>
    </>
  );
}

/** A provider's error in a sentence: its own message, and what to do for the common ones. */
export function plainError(raw: string): string {
  const inner = /"message"\s*:\s*"((?:[^"\\]|\\.)*)"/.exec(raw)?.[1] ?? raw;
  if (/credit balance is too low/i.test(inner))
    return "The Anthropic account is out of credit. Top it up at console.anthropic.com (Plans & Billing), then test again.";
  if (/invalid x-api-key|authentication_error|incorrect api key/i.test(inner))
    return "The AI provider refused the API key. Check the key in Railway (or on this setup), then test again.";
  if (/rate.?limit|429/i.test(inner))
    return "The AI provider is limiting requests right now. Wait a few minutes and test again.";
  return inner;
}

function EvalDetail({ runId, onClose }: { runId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ["operator-ai-eval", runId],
    queryFn: () => api.get<{ run: EvalRun; results: EvalResult[] }>(`/operator/ai/evals/${runId}`),
  });
  return (
    <Dialog
      open
      title={q.data ? `What ${q.data.run.config_name} got wrong` : "Test details"}
      onClose={onClose}
    >
      {q.isError && <ErrorText error={q.error} />}
      {!q.data ? (
        <Loading />
      ) : (
        <ul className="-mx-5 -my-5 divide-y divide-line">
          {[...q.data.results]
            .sort((a, b) => Number(a.ok) - Number(b.ok))
            .map((r) => (
              <li key={`${r.task}:${r.case_id}`} className="px-5 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium">{r.title}</div>
                    <div className="text-xs text-muted">
                      {TASK[r.task]} · {seconds(r.duration_ms)} · {usd(r.cost_micros)}
                      {r.models && ` · ${r.models}`}
                    </div>
                  </div>
                  <Badge tone={r.ok ? "ok" : r.score > 0 ? "warn" : "danger"}>
                    {r.ok ? "All right" : `${pct(r.score)} right`}
                  </Badge>
                </div>
                {r.error && <p className="mt-2 text-sm text-danger">{plainError(r.error)}</p>}
                {r.checks.some((c) => !c.pass) && (
                  <ul className="mt-2 flex flex-col gap-1 text-sm">
                    {r.checks
                      .filter((c) => !c.pass)
                      .map((c) => (
                        <li key={c.field}>
                          <span className="font-medium">{c.field}</span>
                          <span className="text-muted">
                            : should be {show(c.expected)}, recoup read {show(c.got)}
                          </span>
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            ))}
        </ul>
      )}
    </Dialog>
  );
}

// ------------------------------------------------------------------------------- examples

interface Example {
  id: string;
  task: string;
  ref_id: string;
  verdict: string;
  source: "auto" | "person";
  provider: string | null;
  model: string | null;
  signals: Record<string, unknown>;
  note: string | null;
  created_at: string;
  org_name: string;
  config_name: string | null;
}

/** What happened, in words, from an example's signals. */
export function describeSignals(s: Record<string, unknown>): string[] {
  const out: string[] = [];
  const n = (k: string) => Number(s[k] ?? 0);
  const plural = (v: number, one: string, many: string) => `${v} ${v === 1 ? one : many}`;
  if (n("answersChanged"))
    out.push(
      `${plural(n("answersChanged"), "answer", "answers")} changed from recoup's suggestion`,
    );
  if (n("servicesLeftOut"))
    out.push(`${plural(n("servicesLeftOut"), "service", "services")} left out`);
  if (n("termsEdited"))
    out.push(`terms edited on ${plural(n("termsEdited"), "service", "services")}`);
  if (n("feesChanged")) out.push(`${plural(n("feesChanged"), "fee", "fees")} corrected`);
  if (s.vendorRenamed) out.push("vendor renamed");
  if (s.claimsEmailChanged) out.push("claims address corrected");
  if (s.repairedByChecks) out.push("first draft failed a check and was redrafted");
  if (s.editedByPerson) out.push("edited by a person before sending");
  const failing = s.failedChecks as string[] | undefined;
  if (failing?.length) out.push(`still failing: ${failing.join(", ")}`);
  if (s.read && s.confirmed && s.read !== s.confirmed)
    out.push(
      `read as ${String(s.read).replace(/_/g, " ")}, a person said ${String(s.confirmed).replace(/_/g, " ")}`,
    );
  if (s.error) out.push(String(s.error));
  return out;
}

type ExampleList = { examples: Example[]; totals: { task: string; verdict: string; n: number }[] };

const OUTCOMES = {
  all: { label: "All", verdicts: [] as string[] },
  right: { label: "Right", verdicts: ["good", "thumbs_up"] },
  fixed: { label: "Needed fixing", verdicts: ["corrected"] },
  wrong: { label: "Wrong", verdicts: ["failed", "thumbs_down"] },
};

function Examples() {
  const [outcome, setOutcome] = useState<keyof typeof OUTCOMES>("all");
  const q = useQuery({
    queryKey: ["operator-ai-examples"],
    queryFn: () => api.get<ExampleList>("/operator/ai/examples"),
  });
  if (q.isError) return <ErrorText error={q.error} />;
  if (!q.data) return <Loading />;
  if (q.data.examples.length === 0)
    return (
      <Card>
        <Empty title="Nothing yet.">
          When a customer approves a contract, edits a letter or rates what recoup read, it shows up
          here, so you can see where the AI gets things right and where people had to fix it.
        </Empty>
      </Card>
    );
  const want = OUTCOMES[outcome].verdicts;
  const rows = q.data.examples.filter((e) => want.length === 0 || want.includes(e.verdict));
  return (
    <>
      <Segmented
        label="Outcome"
        options={Object.entries(OUTCOMES).map(([id, o]) => ({
          id: id as keyof typeof OUTCOMES,
          label: o.label,
        }))}
        value={outcome}
        onChange={setOutcome}
      />
      <Card flush>
        <Table
          head={["When", "Company", "What", "Result", "What happened"]}
          empty={rows.length === 0 && <Empty title="None of these yet." />}
        >
          {rows.map((e) => {
            const v = VERDICT[e.verdict] ?? { label: e.verdict, tone: "neutral" as const };
            const what = describeSignals(e.signals);
            return (
              <tr key={e.id}>
                <Td className="whitespace-nowrap">
                  <span title={dateTime(e.created_at)}>{date(e.created_at)}</span>
                </Td>
                <Td>{e.org_name}</Td>
                <Td>
                  <div className="whitespace-nowrap">{TASK[e.task] ?? e.task}</div>
                  <div className="text-xs break-words text-muted">
                    {e.config_name ?? "Standard"} · {e.model ?? "—"}
                  </div>
                </Td>
                <Td>
                  <Badge tone={v.tone}>{v.label}</Badge>
                </Td>
                <Td className="max-w-md">
                  {what.length > 0 && <div>{what.join("; ")}</div>}
                  {e.note && <div className="mt-1 whitespace-pre-wrap text-muted">“{e.note}”</div>}
                  {what.length === 0 && !e.note && (
                    <span className="text-muted">Approved as recoup read it</span>
                  )}
                </Td>
              </tr>
            );
          })}
        </Table>
      </Card>
    </>
  );
}
